import React from 'react';
import OnePlayer from "@components/StatsConsole/Widgets/LineupWidget/parts/OnePlayer";
import { IPlayer } from "@components/StatsConsole/Widgets/LineupWidget/parts/Players";

interface DiamondFormationPlayersProps {
    players: IPlayer[];
    rowIndex: number;
    team: 'home' | 'away';
    iframeWidth?: number | null;
}

// Helper function to organize players in diamond formation
export const organizeDiamondFormation = (players: IPlayer[]): { 
    am: IPlayer | null, 
    cm1: IPlayer | null, 
    cm2: IPlayer | null, 
    dm: IPlayer | null 
} | null => {
    if (players.length !== 4) return null;

    let am: IPlayer | null = null;
    let cm1: IPlayer | null = null;
    let cm2: IPlayer | null = null;
    let dm: IPlayer | null = null;

    // Identify players by position
    for (const player of players) {
        const position = player.position?.toLowerCase() || '';
        if (position.includes('attacking_midfielder') || position.includes('attacking midfielder')) {
            am = player;
        } else if (position.includes('defensive_midfielder') || position.includes('defensive midfielder')) {
            dm = player;
        } else if (position.includes('central_midfielder') || position.includes('midfielder')) {
            if (!cm1) {
                cm1 = player;
            } else if (!cm2) {
                cm2 = player;
            }
        }
    }

    // Fallback: use the order from the data
    if (!am || !dm || !cm1 || !cm2) {
        am = players[0];
        cm1 = players[1];
        cm2 = players[2];
        dm = players[3];
    }

    return { am, cm1, cm2, dm };
};

const DiamondFormationPlayers = ({ players, rowIndex, team, iframeWidth }: DiamondFormationPlayersProps) => {
    const diamond = organizeDiamondFormation(players);
    if (!diamond || !diamond.am || !diamond.cm1 || !diamond.cm2 || !diamond.dm) {
        return null;
    }

    const playerWidth = (count: number) => {
        if (iframeWidth && iframeWidth < 349) {
            return `${(iframeWidth - 24) / count}px`;
        }
        if (count === 1) return '343px';
        if (count === 2) return '169.5px';
        return '112px';
    };

    return (
        <>
            {/* Line 1: AM (Attacking Midfielder) - centered */}
            <div key={`${rowIndex}-diamond-am-${team}`} className={`flex justify-center mb-[8px] w-full flex-nowrap
                ${iframeWidth && iframeWidth < 349 ? 'gap-[1px]' : 'gap-[4px]'}
            `}>
                <OnePlayer
                    team={team}
                    key={`${rowIndex}-0-${diamond.am?.name}-${diamond.am.number}-${team}-diamond`}
                    width={playerWidth(1)}
                    player={diamond.am}
                    id={`${rowIndex}-0-${diamond.am?.name}-${diamond.am.number}-${team}-diamond`}
                    longName={true}
                    isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                    isLineup={true}
                />
            </div>
            {/* Line 2: CM, CM (Central Midfielder) - left/right */}
            <div key={`${rowIndex}-diamond-cm-${team}`} className={`flex justify-center mb-[8px] w-full flex-nowrap
                ${iframeWidth && iframeWidth < 349 ? 'gap-[1px]' : 'gap-[4px]'}
            `}>
                <OnePlayer
                    team={team}
                    key={`${rowIndex}-1-${diamond.cm1?.name}-${diamond.cm1.number}-${team}-diamond`}
                    width={playerWidth(2)}
                    player={diamond.cm1}
                    id={`${rowIndex}-1-${diamond.cm1?.name}-${diamond.cm1.number}-${team}-diamond`}
                    longName={true}
                    isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                    isLineup={true}
                />
                <OnePlayer
                    team={team}
                    key={`${rowIndex}-2-${diamond.cm2?.name}-${diamond.cm2.number}-${team}-diamond`}
                    width={playerWidth(2)}
                    player={diamond.cm2}
                    id={`${rowIndex}-2-${diamond.cm2?.name}-${diamond.cm2.number}-${team}-diamond`}
                    longName={true}
                    isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                    isLineup={true}
                />
            </div>
            {/* Line 3: DM (Defensive Midfielder) - centered */}
            <div key={`${rowIndex}-diamond-dm-${team}`} className={`flex justify-center mb-[8px] w-full flex-nowrap
                ${iframeWidth && iframeWidth < 349 ? 'gap-[1px]' : 'gap-[4px]'}
            `}>
                <OnePlayer
                    team={team}
                    key={`${rowIndex}-3-${diamond.dm?.name}-${diamond.dm.number}-${team}-diamond`}
                    width={playerWidth(1)}
                    player={diamond.dm}
                    id={`${rowIndex}-3-${diamond.dm?.name}-${diamond.dm.number}-${team}-diamond`}
                    longName={true}
                    isSmallShirt={iframeWidth && iframeWidth < 349 ? true : false}
                    isLineup={true}
                />
            </div>
        </>
    );
};

export default DiamondFormationPlayers;


